import { docsHref } from '../docsPaths';

const CONSOLE_SIGNUP_URL = 'https://console.pokerworks.io/signup';

// Lead with operator outcomes. Calculation remains the quick on-ramp, but the
// platform thesis is operating poker rooms, tournaments, webhooks, and proofs.
const featureGroups = [
  {
    icon: 'route',
    tone: 'blue',
    title: 'Run your poker room',
    description:
      'Create a PokerDeck room, schedule tournaments, wire webhooks, and verify results.',
    href: docsHref('/automation/rooms'),
  },
  {
    icon: 'key',
    tone: 'purple',
    title: 'Automate with a service account',
    description:
      'Use scoped management tokens from trusted server code, CI, back-office tools, or agents.',
    href: docsHref('/automation/automate-with-a-service-account'),
  },
  {
    icon: 'book',
    tone: 'gold',
    title: 'Choose your API path',
    description: 'Map room operators, partners, tournament organizers, and tool builders to APIs.',
    href: docsHref('/concepts/choose-your-api-path'),
  },
  {
    icon: 'cube',
    tone: 'green',
    title: 'Make a quick calculation call',
    description:
      'Use product-key endpoints for equity, ICM, payouts, blind structures, and chip stacks.',
    href: docsHref('/get-started'),
  },
];

const startCards = [
  {
    icon: 'rocket',
    title: 'Get started',
    description: 'Choose room automation or the fastest first calculation call.',
    href: docsHref('/get-started'),
  },
  {
    icon: 'route',
    title: 'Choose your API path',
    description: 'Choose the API path for room operators, partners, organizers, and tool builders.',
    href: docsHref('/concepts/choose-your-api-path'),
  },
  {
    icon: 'compass',
    title: 'How the API works',
    description: 'Authentication, the response envelope, errors, and usage semantics.',
    href: docsHref('/concepts/how-the-api-works'),
  },
  {
    icon: 'key',
    title: 'Create a console account',
    description: 'Set up projects, management tokens, rooms, and product keys from one console.',
    href: CONSOLE_SIGNUP_URL,
  },
  {
    icon: 'spark',
    title: 'Changelog',
    description: 'Track API, SDK, docs, and beta availability updates.',
    href: docsHref('/changelog'),
  },
  {
    icon: 'route',
    title: 'Recipes',
    description: 'Outcome-led examples for embeds, webhooks, overlays, and automation.',
    href: docsHref('/recipes/overview'),
  },
];

export function DocsHome() {
  return (
    <div className="pw-docs-home">
      <p className="pw-docs-eyebrow">Developer docs</p>
      <h1>PokerWorks Developer Docs</h1>
      <p className="pw-docs-lede">
        The PokerWorks API is the shared platform behind hosted social poker: rooms, tournaments,
        webhooks, verifiable results, and reliable poker math as the fast product-key on-ramp.
      </p>

      <div className="pw-docs-feature-grid" aria-label="PokerWorks API areas">
        {featureGroups.map((item) => (
          <a className="pw-docs-feature-card" href={item.href} key={item.href}>
            <span className={`pw-docs-icon pw-docs-icon-${item.tone}`} aria-hidden="true">
              <HomeIcon name={item.icon} />
            </span>
            <span className="pw-docs-card-copy">
              <strong>{item.title}</strong>
              <small>{item.description}</small>
            </span>
          </a>
        ))}
      </div>

      <h2>Get started</h2>
      <div className="pw-docs-card-grid" aria-label="Getting started links">
        {startCards.map((item) => (
          <a className="pw-docs-card" href={item.href} key={item.href}>
            <span className="pw-docs-link-icon" aria-hidden="true">
              <HomeIcon name={item.icon} />
            </span>
            <span className="pw-docs-card-copy">
              <strong>{item.title}</strong>
              <small>{item.description}</small>
            </span>
          </a>
        ))}
      </div>

      <h2>Explore</h2>
      <div className="pw-docs-card-grid" aria-label="Explore the platform">
        <a className="pw-docs-card" href={docsHref('/automation/service-accounts')}>
          <span className="pw-docs-link-icon" aria-hidden="true">
            <HomeIcon name="key" />
          </span>
          <span className="pw-docs-card-copy">
            <strong>Service accounts</strong>
            <small>
              Management tokens for organizations, projects, rooms, tournaments, and webhooks.
            </small>
          </span>
        </a>
        <a className="pw-docs-card" href={docsHref('/api/management')}>
          <span className="pw-docs-link-icon" aria-hidden="true">
            <HomeIcon name="book" />
          </span>
          <span className="pw-docs-card-copy">
            <strong>Management API</strong>
            <small>
              OpenAPI reference for organization, room, tournament, webhook, and tournament result
              automation.
            </small>
          </span>
        </a>
        <a className="pw-docs-card" href={docsHref('/calculations/overview')}>
          <span className="pw-docs-link-icon" aria-hidden="true">
            <HomeIcon name="cube" />
          </span>
          <span className="pw-docs-card-copy">
            <strong>Calculation API</strong>
            <small>Hold'em equity, ICM deals, payouts, blind structures, and chip stacks.</small>
          </span>
        </a>
        <a className="pw-docs-card" href={docsHref('/sdk')}>
          <span className="pw-docs-link-icon" aria-hidden="true">
            <HomeIcon name="code" />
          </span>
          <span className="pw-docs-card-copy">
            <strong>TypeScript SDK</strong>
            <small>Coming soon as @pokerworks/sdk; beta docs are cURL/OpenAPI-first.</small>
          </span>
        </a>
        <a className="pw-docs-card" href={docsHref('/recipes/overview')}>
          <span className="pw-docs-link-icon" aria-hidden="true">
            <HomeIcon name="route" />
          </span>
          <span className="pw-docs-card-copy">
            <strong>Recipes</strong>
            <small>Copyable outcomes for embeds, webhooks, overlays, and automation.</small>
          </span>
        </a>
        <a className="pw-docs-card" href={docsHref('/api')}>
          <span className="pw-docs-link-icon" aria-hidden="true">
            <HomeIcon name="book" />
          </span>
          <span className="pw-docs-card-copy">
            <strong>Calculation API Reference</strong>
            <small>OpenAPI-backed product-key calculation endpoint documentation.</small>
          </span>
        </a>
      </div>
    </div>
  );
}

function HomeIcon({ name }: { name: string }) {
  switch (name) {
    case 'book':
      return (
        <svg viewBox="0 0 24 24" role="img">
          <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15.5H7A3 3 0 0 0 4 21V5.5Z" />
          <path d="M4 18.5A2.5 2.5 0 0 1 6.5 16H20" />
        </svg>
      );
    case 'code':
      return (
        <svg viewBox="0 0 24 24" role="img">
          <path d="m8 9-4 3 4 3" />
          <path d="m16 9 4 3-4 3" />
          <path d="m13 5-2 14" />
        </svg>
      );
    case 'compass':
      return (
        <svg viewBox="0 0 24 24" role="img">
          <circle cx="12" cy="12" r="9" />
          <path d="m15 9-2 6-6 2 2-6 6-2Z" />
        </svg>
      );
    case 'key':
      return (
        <svg viewBox="0 0 24 24" role="img">
          <circle cx="7.5" cy="12.5" r="3.5" />
          <path d="M11 12.5h9" />
          <path d="M17 12.5v3" />
          <path d="M14 12.5v2" />
        </svg>
      );
    case 'rocket':
      return (
        <svg viewBox="0 0 24 24" role="img">
          <path d="M6 15c-1.8 1-2.7 2.7-3 5 2.3-.3 4-1.2 5-3" />
          <path d="M10 14 7 17l-2-2 3-3" />
          <path d="M13 4c3.2-.9 5.6-.4 7 1-1.4 4.8-4.1 8.5-8 11l-4-4c1.1-3.3 2.8-5.9 5-8Z" />
          <circle cx="15.5" cy="8.5" r="1.5" />
        </svg>
      );
    case 'route':
      return (
        <svg viewBox="0 0 24 24" role="img">
          <circle cx="6" cy="6" r="2" />
          <circle cx="18" cy="18" r="2" />
          <path d="M8 6h4a4 4 0 0 1 0 8H8a4 4 0 0 0 0 8h8" />
        </svg>
      );
    case 'spark':
      return (
        <svg viewBox="0 0 24 24" role="img">
          <path d="M12 3 9.8 9.8 3 12l6.8 2.2L12 21l2.2-6.8L21 12l-6.8-2.2L12 3Z" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" role="img">
          <path d="M12 3 4 7v10l8 4 8-4V7l-8-4Z" />
          <path d="m4 7 8 4 8-4" />
          <path d="M12 11v10" />
        </svg>
      );
  }
}
